import { useState, useEffect } from 'react';

// Module-level store so state survives a component unmounting and remounting
// within a single page session. Dashboard tabs fully unmount when you switch
// away (Dashboard renders one tab at a time via a switch), which otherwise
// discards their filter/pagination state and forces a fresh fetch on return.
//
// Unlike sessionStorage this keeps native values (Date objects, etc.) with no
// JSON serialization, and it intentionally resets on a full page reload.
const store = new Map();

/**
 * Like useState, but seeded from and mirrored to a per-key module-level store so
 * the value persists across unmount/remount within the page session.
 *
 * @template T
 * @param {string} key - Stable key identifying this piece of state.
 * @param {T | (() => T)} initial - Initial value (or lazy initializer) used only
 *   when nothing is stored for `key` yet.
 * @param {{ storage?: 'local' }} [options] - `storage: 'local'` also keeps the
 *   value in localStorage (JSON) so a preference survives a page reload.
 * @returns {[T, import('react').Dispatch<import('react').SetStateAction<T>>]}
 */
export function usePersistentState(key, initial, options = {}) {
  const local = options.storage === 'local';
  const [state, setState] = useState(() => {
    if (store.has(key)) return store.get(key);
    if (local) {
      const saved = readLocal(key);
      if (saved !== undefined) return saved;
    }
    return typeof initial === 'function' ? initial() : initial;
  });

  useEffect(() => {
    store.set(key, state);
    if (local) writeLocal(key, state);
  }, [key, state, local]);

  return [state, setState];
}

const LOCAL_PREFIX = 'pref.';

function readLocal(key) {
  try {
    const raw = window.localStorage.getItem(LOCAL_PREFIX + key);
    return raw == null ? undefined : JSON.parse(raw);
  } catch {
    return undefined;
  }
}

function writeLocal(key, value) {
  try {
    window.localStorage.setItem(LOCAL_PREFIX + key, JSON.stringify(value));
  } catch {
    // Storage full or blocked: the in-memory store still holds the value for this page session.
  }
}
